import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, Mail } from "lucide-react";
import { SITE } from "../data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/services/estate-sales", label: "Estate Sales" },
  { to: "/services/cleanouts-downsizing", label: "Cleanouts & Downsizing" },
  { to: "/upcoming-estate-sales", label: "Upcoming Estate Sales" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isCleanoutPage = location.pathname === "/estate-cleanouts";

  return (
    <header className="sticky top-0 z-50 bg-ivory/95 backdrop-blur-sm border-b border-linen" data-testid="site-header">
      <div className="bg-forest text-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between text-xs sm:text-sm">
          <p className="tracking-wide" data-testid="header-service-area">
            {isCleanoutPage ? "Serving Maryland and Northern Virginia" : `Serving ${SITE.serviceArea}`}
          </p>
          <div className="flex items-center gap-5">
            <a
              href={SITE.emailHref}
              className="hidden sm:inline-flex items-center gap-2 font-semibold hover:text-brass transition-colors"
              data-testid="header-email-link"
            >
              <Mail className="w-3.5 h-3.5" />
              {SITE.email}
            </a>
            <a
              href={SITE.phoneHref}
              className="inline-flex items-center gap-2 font-semibold hover:text-brass transition-colors"
              data-testid="header-phone-link"
            >
              <Phone className="w-3.5 h-3.5" />
              {SITE.phone}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="header-logo" aria-label="MacWay Estate Services — home">
            <img src="/images/logo.png" alt="MacWay Estate Services" className="h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary" data-testid="header-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors hover:text-forest ${
                    isActive ? "text-forest border-b-2 border-brass pb-1" : "text-ink/70"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {isCleanoutPage ? (
              <a href="#cleanout-estimate" className="btn-brass" data-testid="header-quote-cta">
                Request a Cleanout Estimate
              </a>
            ) : (
              <Link to="/contact" className="btn-brass" data-testid="header-quote-cta">
                Request a Quote
              </Link>
            )}
          </div>

          <button
            className="lg:hidden p-2 text-forest"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-linen bg-ivory" aria-label="Mobile" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-semibold transition-colors ${
                    isActive ? "text-forest bg-sand" : "text-ink/75 hover:bg-sand"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {isCleanoutPage ? (
              <a
                href="#cleanout-estimate"
                onClick={() => setOpen(false)}
                className="btn-brass mt-3 justify-center"
                data-testid="mobile-quote-cta"
              >
                Request a Cleanout Estimate
              </a>
            ) : (
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-brass mt-3 justify-center"
                data-testid="mobile-quote-cta"
              >
                Request a Quote
              </Link>
            )}
          </div>
        </nav>
      )}
      <span className="hidden">{location.pathname}</span>
    </header>
  );
};
